import React, { createContext, useContext, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'high-contrast';

interface ThemeContextType {
  theme: Theme;
  fontSize: number;
  setTheme: (theme: Theme) => void;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  resetFontSize: () => void;
  toggleHighContrast: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'edulumina-theme-preference';
const FONT_STORAGE_KEY = 'edulumina-font-size';
const MIN_FONT_SIZE = 12;
const MAX_FONT_SIZE = 24;
const DEFAULT_FONT_SIZE = 16;

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'high-contrast') return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const [fontSize, setFontSize] = useState<number>(() => {
    const saved = localStorage.getItem(FONT_STORAGE_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_FONT_SIZE;
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark', 'high-contrast');
    root.classList.add(theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    const root = window.document.documentElement;
    root.style.setProperty('--font-size-base', `${fontSize}px`);
    localStorage.setItem(FONT_STORAGE_KEY, fontSize.toString());
  }, [fontSize]);

  // Atalho de teclado: Alt + Shift + A
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.shiftKey && e.key.toLowerCase() === 'a') {
        toggleHighContrast();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [theme]);

  const setTheme = (newTheme: Theme) => setThemeState(newTheme);

  const toggleHighContrast = () => {
    setThemeState(prev => prev === 'high-contrast' ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : 'high-contrast');
  };

  const increaseFontSize = () => setFontSize(prev => Math.min(prev + 2, MAX_FONT_SIZE));
  const decreaseFontSize = () => setFontSize(prev => Math.max(prev - 2, MIN_FONT_SIZE));
  const resetFontSize = () => setFontSize(DEFAULT_FONT_SIZE);

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      fontSize, 
      setTheme, 
      increaseFontSize, 
      decreaseFontSize, 
      resetFontSize,
      toggleHighContrast 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};